/**
 * Session claims: the JWT payload minted by auth.sara.ai, and the reader-session cookie derived
 * from it.
 *
 * **Tier 2, so zod is correct here** (`docs/data-architecture/README.md` §5). Everything else in
 * `src/contracts/` is tier 1 — `as const` unions, no zod — because it is vocabulary the codebase
 * both produces and consumes. These schemas parse a bearer token whose bytes arrive from outside
 * the process and must be rejected with a message when malformed, which is exactly the tier-2
 * test. `zod` is already a dependency of this package.
 *
 * Three copies folded in (kanban #16):
 *
 * - `monorepo/src/types/jwt.ts` — the canonical and richest copy, and the **issuer**: auth.sara.ai
 *   is the monorepo (`monorepo/src/lib/auth/jwt.ts` signs and `monorepo/src/app/(subdomains)/auth/*`
 *   serves). Everything it validates, it also mints.
 * - `app/src/lib/types/session.ts` — the reader app's consumer-side copy. Same arms, fewer fields,
 *   looser types, plus the cookie shape and `parseSessionPayload`.
 * - `retells/convex/model/session.ts` — a third read, expressed as a Convex validator. Its header
 *   claims it was verified against the monorepo file; **confirmed** — it is a strict subset of the
 *   `user_session` arm plus `sub`, and contributes no claim the other two lack.
 *
 * ## Two names collided and neither survives as-is
 *
 * `ReaderSessionSchema` meant two different things: in the monorepo it is the `reader_session`
 * **arm of the token**, in the app it is the **cookie** the app writes after verifying a token.
 * Both repos also export a type `ReaderSession` for their respective meaning. Keeping either name
 * would make the ambiguity permanent, so **neither `ReaderSessionSchema` nor `ReaderSession` is
 * exported from here**. The token arm takes the app's own unambiguous name for it —
 * `ReaderSessionPayloadSchema` / `ReaderSessionPayload` — and the cookie is renamed to
 * `ReaderSessionCookieSchema` / `ReaderSessionCookie`, the one new name in this module. The user
 * arm follows the same convention (`UserSessionPayloadSchema`, the app's name; the monorepo called
 * it `UserSessionSchema`). Every original name is recorded on the export it became.
 *
 * ## Unknown claims are tolerated, and dropped
 *
 * All three sources describe a parser that must not reject a token carrying claims it does not
 * know about — `retells/convex/model/session.ts`'s header states it outright. Under zod 4's
 * default `.strip()` object mode that is what all three copies already do: an unrecognised claim
 * does **not** fail the parse, and does **not** appear in the parsed output. That behavior is
 * preserved exactly, not tightened to `.strict()` and not loosened to `.passthrough()`, and
 * `session.test.ts` asserts both halves of it. A caller that needs a claim must add it here.
 *
 * ## Registered claims sit on the arms, and `JWTPayloadSchema` narrows two of them
 *
 * The three copies disagreed on where the standard JWT claims live and which are required: the
 * monorepo put them in a wrapper object intersected onto the union, with `sub` and `aud`
 * **required**; the app put `aud`/`iat`/`exp` directly on each arm, all optional, and had no `sub`
 * at all; retells had `sub` optional on the arm. Rather than pick, both postures are kept as two
 * named schemas, which is what the sources already were:
 *
 * - `SessionPayloadSchema` — the union, each arm carrying every registered claim as optional. This
 *   is the app's parse entry point and its output is unchanged: `aud`/`iat`/`exp` still survive
 *   the parse.
 * - `JWTPayloadSchema` — the same union with `sub` and `aud` required, which is the monorepo's
 *   verify-side contract and is true of every token the issuer mints (`signSessionData` sets both
 *   unconditionally, with `sub` equal to `workosId` for a user session and to `readerId` for a
 *   reader session).
 *
 * ## What did not come across
 *
 * The monorepo hand-wrote its `UserSession` type beside the schema and pinned the two together
 * with an `AssertExact` compile-time check, solely so the `workosUserId` alias could be spelled on
 * `workosId`. Only the inferred type is declared here, so there is no second declaration to drift
 * from and nothing for the assertion to guard; the alias is exported on its own as
 * `WorkosUserId`. No field, value or optionality changed in the fold. The app's `SessionIdentity`
 * / `SessionUiState` / `Session` interfaces are reader-app client state (Legend State
 * observables), not a claim in any token or cookie, and deliberately do not port.
 *
 * The array claims deliberately do **not** carry zod's `.readonly()`. It reads like the
 * type-level modifier on {@link Result}, and it is not one: `.readonly()` calls `Object.freeze()`
 * on the parse output, so it changes what a verified token produces at runtime and would make a
 * consumer that sorts `readerNames` in place throw. Neither source freezes, so neither does this.
 * Raised twice by the self-audit and skipped for this reason both times.
 *
 * ## The PII split, and the one field that survived it
 *
 * The port above froze the `user_session` arm as it found it — `email`, `firstName`, `lastName`,
 * `profilePictureUrl` and a bearer `accessToken`, PII inside a credential handed to every
 * consumer. Kanban #88 changed that, and it is the single site the port existed to create.
 *
 * **`email`, `lastName`, `profilePictureUrl` and `accessToken` are gone.** A consumer that wants
 * a person's name or address asks `apps/secure` for `GET /secure/pii/:id` or
 * `GET /secure/email/:id` under its own service token, with the id read from `convex-identity`'s
 * `piiReference` / `emailReference`. The values live in the `enterprise` CockroachDB database and
 * nowhere else (`docs/data-architecture/README.md` §4), and `apps/auth`'s `/callback` is what puts
 * them there. Under zod's `.strip()` mode a legacy token still carrying these claims parses fine
 * and simply loses them, so nothing breaks on a cookie minted before the cutover.
 *
 * **`firstName` stayed, optional and `@deprecated`, for one release.** The inventory #88 required
 * — every reader of `getUserSessionPayload` in `../app` — found the other four claims read by
 * nothing at all, and `firstName` read on a live path: it is the display name `client.join()`
 * hands Zoom in an orientation session (`app/src/backend/actions/zoom-signature.ts`,
 * `app/src/app/orientation/[sessionId]/page.tsx`), and the ONLY source for an aide's name — a
 * reader's falls back to `readerNickname`, an aide's falls back to the literal `"Aide"`. Removing
 * it in the same PR would have been a silent product regression in a live video call, so the
 * compatibility window is the honest trade and not a hedge.
 *
 * **What retires it.** `../app` reading the given name from `apps/secure` — the `piiId` is already
 * in the identity plane, so nothing else has to be built first. When that lands, delete the field
 * here and stop minting it in `apps/auth/worker/routes/callback.ts`; nothing else in this
 * workspace reads it (`@bindery/auth-consumer` narrows this arm to a key-by-key `Pick` of `sub`,
 * `readerId`, `internalRole` and `readerNames` that leaves `firstName` out, on purpose and for this
 * reason).
 *
 * `readerNames` stays, and that is a decision rather than an omission: it is a
 * readerId-to-**public**-display-name join, and `readerNickname` is a public display name by
 * design (`docs/data-architecture/README.md` §4), on both arms and in the cookie. It is not PII
 * and no caution belongs on it. Whether a bearer token should carry a join table at all is a
 * separate question, and #88 did not answer it.
 */

// Namespace import, not `import { z }` — zod ships CJS for the node condition
// and the named form resolves to `undefined` under vitest's multi-project run.
// Same fix as packages/logging/src/ingest.ts and
// packages/convex-retells/convex/model/receipt.ts; it is zod 4's documented import form
// anyway. The ported line used the named form because bindery-source writes it
// that way.
import * as z from "zod";
import { ReaderIdSchema } from "../auth/readerId";
import { READER_SESSION_TYP, USER_SESSION_TYP } from "./session-typ";

export {
  READER_SESSION_TYP,
  sessionTypOf,
  type SessionTyp,
  USER_SESSION_TYP,
} from "./session-typ";

// ---------------------------------------------------------------------------
// URL parameters on the auth redirect
// ---------------------------------------------------------------------------

/** Where to send the browser after `/auth/redirect` completes. */
export const RETURN_TO_PARAM = "returnTo";

/** Reader to bind the minted session to. Parsed as a UUID by the redirect route. */
export const READER_ID_PARAM = "readerId";

/** Set to the string `"true"` to have the redirect include `readerNames` in the token. */
export const INCLUDE_READERS_PARAM = "includeReaders";

// ---------------------------------------------------------------------------
// Claim components
// ---------------------------------------------------------------------------

/**
 * Stable WorkOS user id. The monorepo spells this alias `workosUserId`; renamed to satisfy the
 * repo's type-name casing. Whether WorkOS remains the identity provider is unresolved — kanban #7.
 */
export type WorkosUserId = string;

/**
 * One entry of the multi-reader account switcher's roster.
 *
 * From the app's `ReaderNameSchema`; the monorepo declared the same shape inline inside
 * `UserSessionSchema.readerNames`. `readerNickname` is a public display name, not PII — but the
 * array as a whole is a readerId-to-public-name join, which #88 examined and deliberately left in
 * the token — see the header.
 */
export const ReaderNameSchema = z.object({
  readerId: ReaderIdSchema,
  readerNickname: z.string().optional(),
});

export type ReaderName = z.infer<typeof ReaderNameSchema>;

/**
 * A staff member's role at one district or school.
 *
 * From the monorepo's `OrgMembershipSchema`, which is the only copy that has it. The app and
 * retells both omit `orgMemberships` entirely.
 */
export const OrgMembershipSchema = z.object({
  organizationId: z.string(),
  orgName: z.string(),
  orgType: z.enum(["district", "school"]),
  role: z.enum([
    "teacher",
    "school_admin",
    "district_admin",
    "aide",
    "observer",
  ]),
});

export type OrgMembership = z.infer<typeof OrgMembershipSchema>;

/**
 * The standard JWT claims, all optional.
 *
 * Every arm below extends this, which is how the app's arm-level `aud`/`iat`/`exp` and retells'
 * arm-level `sub` are preserved. `JWTPayloadSchema` re-narrows `sub` and `aud` to required.
 */
export const RegisteredClaimsSchema = z.object({
  /** Subject. Equals `workosId` on a `user_session` and `readerId` on a `reader_session`. */
  sub: z.string().optional(),
  aud: z.string().optional(),
  iss: z.string().optional(),
  exp: z.number().optional(),
  nbf: z.number().optional(),
  iat: z.number().optional(),
  jti: z.string().optional(),
  scopes: z.array(z.string()).optional(),
});

export type RegisteredClaims = z.infer<typeof RegisteredClaimsSchema>;

// ---------------------------------------------------------------------------
// The two arms
// ---------------------------------------------------------------------------

/**
 * Full identity session, minted when a person signs in through the IdP.
 *
 * Monorepo `UserSessionSchema`, folded with the app's `UserSessionPayloadSchema` and retells'
 * `sessionPayloadValidator`. The monorepo copy is the superset: every claim the other two carry is
 * here, and `readerNickname`, `accessToken`, `internalRole` and `orgMemberships` are in no other
 * zod copy (retells has `readerNickname` and `internalRole`, and documents the rest as claims a
 * raw-token parser must tolerate).
 *
 * `email`, `lastName`, `profilePictureUrl` and the bearer `accessToken` were REMOVED by kanban #88
 * — see "The PII split, and the one field that survived it" in the header. `firstName` is the one
 * that remains, deprecated, for one release.
 *
 * **WARNING: `firstName` is PII.** Never log it; correlate on `readerId` or `workosId` instead.
 * The caution is the app copy's, kept verbatim in substance. `readerNickname` is exempt: it is a
 * public display name.
 */
export const UserSessionPayloadSchema = RegisteredClaimsSchema.extend({
  typ: z.literal(USER_SESSION_TYP),
  workosId: z.string(),
  /**
   * The person's given name. **PII, and deprecated — do not add a reader.**
   *
   * Kept for one release only, because `../app` renders it: it is the Zoom display name in an
   * orientation session and the only source an aide has. Retires the moment that app reads the
   * name from `apps/secure` instead. See the header section named for it. Undefined when the IdP
   * supplied no given name at sign-in.
   *
   * @deprecated Read the name from `GET /secure/pii/:id` with the `piiId` the identity plane
   * holds. This claim is removed once `../app` no longer reads it.
   */
  firstName: z.string().optional(),
  /** Currently active reader. */
  readerId: ReaderIdSchema.optional(),
  /**
   * Roster for a multi-reader account.
   *
   * STAYS, and #88 decided that deliberately. `readerNickname` is a public display name by design
   * (`docs/data-architecture/README.md` §4), so this array is a readerId-to-public-name join and
   * not a PII carrier. Whether a bearer token should hold a join table at all is a separate
   * question from whether it may hold PII, and #88 answered only the second.
   */
  readerNames: z.array(ReaderNameSchema).optional(),
  /** Public display name for the currently active reader. Not PII. */
  readerNickname: z.string().optional(),
  /**
   * Platform-internal role. This is the sara-platform role, **not** any one service's admin
   * capability — retells authorizes its own admin actions against its `admins` table and
   * deliberately does not gate on this claim. Surfaced so a consumer can read it; gating on it is
   * a decision, not a default.
   */
  internalRole: z
    .enum(["founder", "admin", "support", "researcher"])
    .optional(),
  orgMemberships: z.array(OrgMembershipSchema).optional(),
});

export type UserSessionPayload = z.infer<typeof UserSessionPayloadSchema>;

/**
 * The credential the broker verified when it minted a `reader_session`: a Google sign-in, a
 * reader token (card), an adult's WorkOS session at `/link-reader`, an LTI launch, or nothing at all
 * (`/reader-session`). A renewal carries the renewed token's value forward.
 */
export const ReaderSessionMethodSchema = z.enum([
  "google",
  "reader_token",
  "workos",
  "lti",
  "anonymous",
]);

export type ReaderSessionMethod = z.infer<typeof ReaderSessionMethodSchema>;

/**
 * Lightweight session for a reader who has no sign-in of their own.
 *
 * Monorepo `ReaderSessionSchema` (type `ReaderSession`) folded with the app's
 * `ReaderSessionPayloadSchema` — the app's name is used because the monorepo's collides with the
 * cookie schema below. Carries no PII, and never did — after #88 the `user_session` arm above is
 * down to one deprecated PII claim, so the two arms differ in capability rather than in kind.
 */
export const ReaderSessionPayloadSchema = RegisteredClaimsSchema.extend({
  typ: z.literal(READER_SESSION_TYP),
  readerId: ReaderIdSchema,
  /** Public display name. Not PII. */
  readerNickname: z.string().optional(),
  /** Whether any user account is linked; absent when the broker could not tell. */
  linked: z.boolean().optional(),
  /**
   * Optional only because tokens minted before the claim existed (at most 30 days old) lack it;
   * a consumer gating paid work treats an absent method as `anonymous`.
   */
  method: ReaderSessionMethodSchema.optional(),
  /**
   * On a `google` session only: an opaque, stable id for the Google account behind it, the same
   * for every reader that account holds. A keyed hash of the identity, never the identity hash
   * itself. A consumer that meters paid work per credential keys on it rather than on `readerId`.
   */
  principal: z.string().min(1).optional(),
});

export type ReaderSessionPayload = z.infer<typeof ReaderSessionPayloadSchema>;

// ---------------------------------------------------------------------------
// The union
// ---------------------------------------------------------------------------

/**
 * Any session payload, discriminated on `typ`.
 *
 * The app's `SessionPayloadSchema`; the monorepo called the same union `SessionDataSchema` (type
 * `SessionData`) and used it as both the signing input and the base of `JWTPayloadSchema`.
 *
 * Narrow on `typ` to reach an arm's fields:
 *
 * ```ts
 * const payload = SessionPayloadSchema.parse(data);
 * if (payload.typ === "user_session") {
 *   // workosId, orgMemberships, …
 * } else {
 *   // readerId, readerNickname
 * }
 * ```
 *
 * Prefer {@link parseSessionPayload} over `.parse()`, which throws a `ZodError` on a malformed
 * token.
 */
export const SessionPayloadSchema = z.discriminatedUnion("typ", [
  UserSessionPayloadSchema,
  ReaderSessionPayloadSchema,
]);

export type SessionPayload = z.infer<typeof SessionPayloadSchema>;

/**
 * A payload as actually issued: {@link SessionPayloadSchema} with `sub` and `aud` required.
 *
 * The monorepo's `JWTPayloadSchema`, which is the shape its verify path enforces. Use this when
 * validating a token off the wire; use {@link SessionPayloadSchema} when the registered claims
 * have already been checked by the JWT library (the app verifies `iss` and `aud` through `jose`
 * before parsing, which is why its copy does not require them again).
 */
export const JWTPayloadSchema = SessionPayloadSchema.and(
  z.object({
    sub: z.string(),
    aud: z.string(),
  }),
);

export type JWTPayload = z.infer<typeof JWTPayloadSchema>;

// ---------------------------------------------------------------------------
// Safe parse
// ---------------------------------------------------------------------------

/**
 * Result of an operation that can fail, preferred over throwing for expected error cases.
 *
 * Ported alongside {@link parseSessionPayload}, which returns it. Both repos had already
 * converged on this exact shape independently (`app/src/lib/types/session.ts`,
 * `monorepo/src/lib/try-catch.ts`), so the fold had nothing to reconcile. It is general-purpose
 * and lives here only because its one consumer does; if a second module needs it, move it.
 *
 * The `readonly` modifiers are the one thing neither source had. They are type-level only — no
 * value, shape or narrowing changes, and constructing one is unaffected — and mutating a parse
 * outcome was never the intent this type expresses.
 */
export type Result<T, E = Error> =
  | { readonly ok: true; readonly data: T; readonly error: null }
  | { readonly ok: false; readonly data: null; readonly error: E };

/**
 * Parse a session payload with explicit error handling.
 *
 * Use instead of `SessionPayloadSchema.parse()` when a malformed token is an expected outcome
 * rather than a bug — an expired cookie, a truncated redirect, a missing `typ`.
 *
 * ```ts
 * const result = parseSessionPayload(jwtPayload);
 * if (!result.ok) return unauthorized(result.error.issues);
 * ```
 */
export function parseSessionPayload(
  data: unknown,
): Result<SessionPayload, z.ZodError> {
  const parsed = SessionPayloadSchema.safeParse(data);
  if (parsed.success) {
    return { ok: true, data: parsed.data, error: null };
  }
  return { ok: false, data: null, error: parsed.error };
}

// ---------------------------------------------------------------------------
// The cookie
// ---------------------------------------------------------------------------

/**
 * The reader-session cookie the app writes after verifying a token, and re-validates on read
 * because a cookie is untrusted input.
 *
 * The app's `ReaderSessionSchema` (type `ReaderSession`), **renamed** — the monorepo used that
 * name for the token arm above. This is not a claim set and is not signed; it is derived from
 * either arm, which is what `method` records. Two details that look like drift and are not:
 * the reader's id is spelled `uuid` here and `readerId` in every token, and `method` repeats the
 * two `typ` values with `"anonymous"` added for a reader who never authenticated at all.
 *
 * The app also migrates a legacy `nickname` key to `readerNickname` before parsing
 * (`app/src/lib/auth.ts`), and separately enforces a 30-day age limit on `authenticatedAt`.
 * Neither is expressed in the schema in the source and neither is added here.
 */
export const ReaderSessionCookieSchema = z.object({
  uuid: z.string(),
  /** Public display name. Not PII. */
  readerNickname: z.string().optional(),
  /** Epoch milliseconds. */
  authenticatedAt: z.number(),
  method: z.enum(["user_session", "reader_session", "anonymous"]),
});

export type ReaderSessionCookie = z.infer<typeof ReaderSessionCookieSchema>;
